import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { Heading4, Heading5, Body, Label } from '~/components/atom/Typography';
import Button from '~/components/atom/Button';
import TextButton from '~/components/atom/TextButton';
import Input from '~/components/molecule/Input';
import Textarea from '~/components/molecule/Textarea';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Icon from '~/components/atom/Icon';
import Divider from '~/components/atom/Divider';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import type { LfaGeneratedCredentialFieldsFragment } from '~/graphql/types';
import type { ServicesV2Action } from '../../../reducers/servicesV2Reducer';
import SectionContainer from '~/components/page/Settings/LFASettings/components/UpdateSettingsForm/components/SectionContainer';

const text = {
  header: 'Gedeelde inloggegevens',
  addCredential: 'Inloggegevens toevoegen',
  nameLabel: 'Naam',
  namePlaceholder: 'Bijv. Google Agenda kantoor',
  usernameLabel: 'Gebruikersnaam',
  passwordLabel: 'Wachtwoord',
  noteLabel: 'Notitie (markdown)',
  notePlaceholder: 'Voeg een notitie toe...',
  deleteCredential: 'Verwijderen',
  newCredential: 'Nieuwe inloggegevens',
  save: 'Opslaan',
  showPassword: 'Tonen',
  hidePassword: 'Verbergen',
  noCredentials: 'Geen inloggegevens geconfigureerd.',
};

const PasswordField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const [revealed, setRevealed] = useState(false);

  return (
    <JustificationContainer align="end" gap="xs" width="100%">
      <Input
        type={revealed ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        label={{ text: label }}
        width="100%"
      />
      <RevealButton
        size="small"
        appearance="secondary"
        label={revealed ? text.hidePassword : text.showPassword}
        onClick={() => setRevealed(!revealed)}
      />
    </JustificationContainer>
  );
};

const CredentialModal: React.FC<{
  credential: LfaGeneratedCredentialFieldsFragment;
  onUpdate: (credential: LfaGeneratedCredentialFieldsFragment) => void;
  onDelete: () => void;
  onClose: () => void;
}> = ({ credential, onUpdate, onDelete, onClose }) => {
  const canSave =
    credential.name.trim() !== '' &&
    credential.username.trim() !== '' &&
    credential.password.trim() !== '';

  return (
    <Overlay onClose={onClose}>
      <Modal maxWidth="480px">
        <JustificationContainer
          direction="column"
          gap="l"
          padding={['l']}
          width="100%"
        >
          <Heading5 margin={[null]}>
            {credential.name || text.newCredential}
          </Heading5>

          <Input
            type="text"
            value={credential.name}
            onChange={e => onUpdate({ ...credential, name: e.target.value })}
            label={{ text: text.nameLabel }}
            placeholder={text.namePlaceholder}
            width="100%"
          />

          <Input
            type="text"
            value={credential.username}
            onChange={e =>
              onUpdate({ ...credential, username: e.target.value })
            }
            label={{ text: text.usernameLabel }}
            width="100%"
          />

          <PasswordField
            value={credential.password}
            onChange={v => onUpdate({ ...credential, password: v })}
            label={text.passwordLabel}
          />

          <JustificationContainer direction="column" gap="xxxs" width="100%">
            <Label size="s" margin={[null]}>
              {text.noteLabel}
            </Label>
            <Textarea
              name="credential-note"
              value={credential.note ?? ''}
              onChange={value =>
                onUpdate({ ...credential, note: value || null })
              }
              placeholder={text.notePlaceholder}
            />
          </JustificationContainer>

          <Divider margin={[null]} />

          <JustificationContainer
            justification="space-between"
            align="center"
            width="100%"
          >
            <Button
              appearance="danger"
              size="small"
              label={text.deleteCredential}
              icon="trashcan"
              onClick={() => {
                onDelete();
                onClose();
              }}
            />
            <Button
              appearance="secondary"
              size="small"
              label={text.save}
              onClick={onClose}
              disabled={!canSave}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

type Props = {
  credentials: Array<LfaGeneratedCredentialFieldsFragment>;
  dispatch: React.Dispatch<ServicesV2Action>;
};

const CredentialsSection: React.FC<Props> = ({ credentials, dispatch }) => {
  const [editingId, setEditingId] = useState<string | null>(null);

  const editingCredential = editingId
    ? credentials.find(c => c.id === editingId)
    : null;

  const handleAdd = () => {
    const id = uuid();
    dispatch({ type: 'V2_ADD_CREDENTIAL', payload: { id } });
    setEditingId(id);
  };

  const handleUpdate = (credential: LfaGeneratedCredentialFieldsFragment) =>
    dispatch({
      type: 'V2_UPDATE_CREDENTIAL',
      payload: { credentialId: credential.id, credential },
    });

  return (
    <SectionContainer gap="base">
      <Heading4 size="m" margin={[null]}>
        {text.header}
      </Heading4>

      {credentials.length === 0 && (
        <Body size="s" color={{ group: 'tertiary' }}>
          {text.noCredentials}
        </Body>
      )}

      <JustificationContainer
        width="100%"
        gap="s"
        style={{ display: 'flex', flexWrap: 'wrap' }}
      >
        {credentials.map(credential => (
          <CredentialCard
            key={credential.id}
            onClick={() => setEditingId(credential.id)}
          >
            <JustificationContainer
              align="center"
              justification="space-between"
              width="100%"
            >
              <JustificationContainer direction="column" gap="xxxs">
                <Label size="base" margin={[null]}>
                  {credential.name || text.newCredential}
                </Label>
                <Body size="s" color={{ group: 'tertiary' }} margin={[null]}>
                  {credential.username}
                </Body>
              </JustificationContainer>
              <Icon name="chevron-right" />
            </JustificationContainer>
          </CredentialCard>
        ))}
      </JustificationContainer>

      <TextButton
        size="small"
        label={text.addCredential}
        icon="plus"
        underline
        onClick={handleAdd}
      />

      {editingCredential && (
        <CredentialModal
          credential={editingCredential}
          onUpdate={handleUpdate}
          onDelete={() =>
            dispatch({
              type: 'V2_REMOVE_CREDENTIAL',
              payload: { credentialId: editingCredential.id },
            })
          }
          onClose={() => setEditingId(null)}
        />
      )}
    </SectionContainer>
  );
};

const CredentialCard = styled.div(
  ({ theme }) => css`
    display: flex;
    flex: 0 0 auto;
    width: max-content;
    min-width: 200px;
    padding: ${theme.spacing('s')} ${theme.spacing('m')};
    background-color: ${theme.color('white')};
    border-radius: ${theme.getTokens().border.radius.m};
    box-shadow: ${theme.getTokens().boxShadow.card};
    outline: 1px solid ${theme.color('tertiary', 'light')};
    cursor: pointer;
    transition:
      transform 0.2s ease-out,
      background-color 0.15s ease;

    &:hover {
      background-color: ${theme.color('tertiary', 'translucent')};
      transform: translateY(-5px);
    }
  `,
);

const RevealButton = styled(Button)(
  () => css`
    flex-shrink: 0;
  `,
);

export default CredentialsSection;
